import React, { useMemo, useState } from "react";
import PropTypes from 'prop-types';
import { useAuth } from "../../providers/AuthProvider";
import LoadingBanner from "../../components/LoadingBanner";
import { Link, useLocation, useSearchParams, useNavigate } from "react-router-dom";
import ErrorBanner from "../../components/ErrorBanner";
import CandidateProvider from "../../providers/CandidateProvider";
import { usePosition } from "../../providers/ProjectPositionProvider";
import { useProject } from "../../providers/ClientProjectProvider";
import CardList from "../../components/CardList";
import PositionCostOverview from "./components/PositionCostOverview";
import ShortlistedCandidateList from "./components/ShortlistedCandidateList";
import InfoTooltip from "../../components/InfoTooltip";
import CandidateTile from "../talent/CandidateTile";
import PositionFormProvider from "../../providers/PositionFormProvider";
import ProjectFormProvider, { useProjectForm } from "../../providers/ProjectFormProvider";
import {Empty, Select, Button, Tooltip} from "antd";
import { EditOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { useAPI } from "../../config/API";
import HideTalentPricingTooltip from "../clients/HideTalentPricingTooltip";
import QuickHireOpenToTalentModal from "./components/QuickHireOpenToTalentModal";
import CandidateTileRedesign, { CandidateTileRedesignSkeleton } from "../talent/CandidateTileRedesign";

const ViewOptions = {
    List: false,//'fa-list',
    Cards: 'fa-th-large'
}

// Unified color scheme
const THEME = {
    bg: '#f0f4f8',
    accent: '#2563eb',
    accentLight: '#60a5fa',
    success: '#10b981',
    text: '#1e293b',
    textLight: '#64748b',
    border: '#c1c6ce'
};

// Option 1: Horizontal Progress Bar
export const ProgressBarStatus = ({ stage }) => {
    const stages = [
        { label: 'Accepting Proposals', value: 1 },
        { label: 'Interviewing Started', value: 2 },
        { label: 'Finalizing Candidate', value: 3 }
    ];

    const progress = (stage / 3) * 100;

    return (
        <div style={{
            padding: '8px 12px',
            background: THEME.bg,
            borderRadius: '8px',
            border: `1px solid ${THEME.border}`
        }}>
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '6px'
            }}>
                <span style={{
                    fontSize: '11px',
                    fontWeight: '600',
                    color: THEME.text,
                    letterSpacing: '0.02em',
                    fontFamily: 'system-ui, -apple-system, sans-serif'
                }}>
                    {stages[stage - 1].label}
                </span>
                <span style={{
                    fontSize: '10px',
                    color: THEME.textLight,
                    fontWeight: '500'
                }}>
                    {stage}/3
                </span>
            </div>
            <div style={{
                height: '4px',
                background: '#e2e8f0',
                borderRadius: '2px',
                overflow: 'hidden',
                position: 'relative'
            }}>
                <div style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    height: '100%',
                    width: `${progress}%`,
                    background: `linear-gradient(90deg, ${THEME.accent}, ${THEME.accentLight})`,
                    borderRadius: '2px',
                    transition: 'width 0.5s cubic-bezier(0.4, 0, 0.2, 1)'
                }} />
            </div>
        </div>
    );
};

export const LISTING_STATUS_OPTIONS = [
    {
        value: 1,
        label: 'Accepting Proposals',
        description: 'Actively reviewing and accepting candidate proposals',
        colorClasses: 'bg-green-100 text-green-800 border-green-300',
        colorClasses2: 'bg-green-200 text-green-800 border-green-400',
        colorClasses3: 'bg-green-600 text-white border-green-400',
    },
    {
        value: 2,
        label: 'Interviewing Started',
        // description: 'Currently conducting interviews with shortlisted candidates',
        description: 'Currently conducting interviews. Proposals still being considered',
        colorClasses: 'bg-purple-100 text-purple-800 border-purple-300',
        colorClasses2: 'bg-green-600 text-white border-green-400',
        colorClasses3: 'bg-purple-600 text-white border-purple-400',
    },
    {
        value: 3,
        label: 'Candidate Being Finalized',
        description: 'In final stages of selecting and onboarding the chosen candidate',
        colorClasses: 'bg-blue-100 text-blue-800 border-blue-300',
        colorClasses2: 'bg-blue-600 text-white border-blue-400',
        colorClasses3: 'bg-amber-600 text-white border-amber-400',

    }
];

const ListingStatusBadge = ({ position, updatePosition, canEdit }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [selectedStatus, setSelectedStatus] = useState(position.listing_status || 1);
    const [isUpdating, setIsUpdating] = useState(false);

    const currentStatus = LISTING_STATUS_OPTIONS.find(
        opt => opt.value === (position.listing_status || 1)
    );

    const handleSubmit = async () => {
        setIsUpdating(true);
        try {
            await updatePosition({ ...position, listing_status: selectedStatus });
            // TB.toast("Position Updated", { type: 'success' });
            setIsEditing(false);
        } catch (error) {
            TB.showServerErrorToast();
        } finally {
            setIsUpdating(false);
        }
    };

    const handleCancel = () => {
        setSelectedStatus(position.listing_status || 1);
        setIsEditing(false);
    };

    if (isEditing) {
        return (
            <div className="flex flex-col gap-3">
                <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-gray-700">Hiring Stage:</span>
                    <div className="flex items-center gap-2">
                        <Select
                            value={selectedStatus}
                            onChange={setSelectedStatus}
                            disabled={isUpdating}
                            style={{ minWidth: 250 }}
                            options={LISTING_STATUS_OPTIONS.map(opt => ({
                                value: opt.value,
                                label: opt.label
                            }))}
                        />
                        <Button
                            type="primary"
                            icon={<CheckOutlined />}
                            onClick={handleSubmit}
                            loading={isUpdating}
                            size="small"
                        />
                        <Button
                            icon={<CloseOutlined />}
                            onClick={handleCancel}
                            disabled={isUpdating}
                            size="small"
                        />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="flex items-center gap-3">
            <span className="text-sm font-semibold text-gray-700">Hiring Stage:</span>
            <div className="flex items-center gap-2 relative">
                {/* <div className={`inline-flex items-center px-3 py-1.5 rounded-md border ${currentStatus.colorClasses}`}>
                    <div className="flex flex-col">
                        <span className="text-sm font-semibold">{currentStatus.label}</span>
                        <span className="text-xs opacity-90 max-w-48">{currentStatus.description}</span>
                    </div>
                </div> */}
                <Tooltip
                    placement="top"
                    color="#000"
                    title={(
                        <p className="text-white italic text-xs">
                            {currentStatus.description}
                        </p>
                    )}
                >
                    <div className="flex relative">
                        <ProgressBarStatus stage={currentStatus.value} />
                    </div>

                </Tooltip>
                    {canEdit && (
                    <EditOutlined
                        className="cursor-pointer hover:opacity-70 text-gray-600"
                        onClick={() => setIsEditing(true)}
                    />
                )}
            </div>
        </div>
    );
};

const ShortlistSelectedCandidate = (props) => {

    const { position } = usePosition();

    const { auth } = useAuth();
    const { project, isLoading, formModel } = useProjectForm();

    if (isLoading || !formModel || !position) return null;

    return (
        <PositionFormProvider positionId={position.id}>
            <PositionCostOverview
                canEdit={false}
                isDisabled={props.isDisabled} />
        </PositionFormProvider>
    );
}
const PositionShortlistedCandidates = (props) => {
    const { position, isLoading: isPositionUpdating, displayIndex, deletePosition, updatePosition, reopenPosition, isDisabled } = usePosition();
    const { project, isProjectUpdating, postUpdate: postUpdateProject, unlinkAppSheetForProjectPosition } = useProject();
    const [searchParams, setSearchParams] = useSearchParams()
    const [activeView, setActiveView] = useState('List');
    const [showQuickHireModal, setShowQuickHireModal] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    const { auth } = useAuth();

    let { onRequest } = props;

    const stats = useMemo(() => {
        let result = project.isFixedFee() ? {} : (position.isFixedRetainer() ? {
            [`EST. COST PER ${position.payout_frequency === 'weekly' ? 'WEEK' : 'MONTH'}`]: auth.canViewTalentPricing() ? (`${TB.formatCurrency(position.calculateFixedRetainerBudgetPerPeriod())}`) : <HideTalentPricingTooltip />,
        } : {
            'EST. HOURLY RATE': auth.canViewTalentPricing() ? (`${TB.formatCurrency(position.platform_rate)}/hr`) : <HideTalentPricingTooltip />,
        })
        if (project.isTimeMaterials()) {
            result['EST. HOURS PER WEEK'] = `${position.avg_hours_per_period} hours`;
        }
        if (project.isFixedFee()) {
            result['EST. TOTAL HOURS'] = `${position.estimated_total_periods} hours`;
        }

        return {
            ...result,
            [project.isFixedFee() ? 'TOTAL ORIGINAL ESTIMATED FEE' : 'TOTAL ESTIMATED FEE']: auth.canViewTalentPricing() ? (TB.formatCurrency(position.platform_cost)) : <HideTalentPricingTooltip />,
            'START AND END DATE': position.timeRange()
        }
    }, [
        position?.assigned_candidate?.id,
        position.actual_total_periods,
        position.estimated_total_periods,
        position.payout_amount,
        position.platform_cost,
        position.platform_rate,
        position.total_selected_candidates,
        position.interested_candidates_count
    ]);
    const hasBeenFilled = position?.assigned_candidate?.id;
    const overrideShowShortlist = project.isActive();

    const {
        data: candidateData,
        isFetching,
        isInitialLoading: isLoading,
        isRefetching,
        error,
        refetch
    } = useAPI(['positionMatches', {
        project_position: props.position.id,
        accomplished_disciplines: TB.map(props.position.disciplines, 'id'),
        shortlisted: 1,
        per_page: 500,
        capture_candidates_request: 1
    }], {
        enabled: !hasBeenFilled || overrideShowShortlist,
        select: data => {
            if (data?.users?.length) {
                let users = TB.orderBy(data.users, user => {
                    if (user.declinedPosition()) return 0;
                    if (user.isAssigned()) return 7;
                    if (user.statusIsApproved() && !user.isFixedFee()) return 6;
                    if (user.isFixedFee() && user.hasConfirmedPayout() && user.hasConfirmedScope()) return 5;
                    if (user.isFixedFee() && user.hasConfirmedScope()) return 4;
                    if (user.matchStatusIsInterested()) return 3;
                    if (user.statusIsPending()) return 2;

                    return 1;
                }, 'desc')

                return {
                    ...data, users
                }
            }
            return data;
        },
        onSuccess: data => {
            if (project.isBidding() && project.isHiringSourceQuickHire() && !project.isHiringSourceQuickHireOpenToTalent()) {
                let quickHireCandidate = TB.first(data.users);
                if (quickHireCandidate?.declinedPosition?.() && !isDisabled) {
                    setShowQuickHireModal(true);
                }

            }
        }
    })

    const canAddRemoveProjectPositions = !project.isActive() && auth?.canAddRemoveProjectPositions(project);
    const canInviteTalentForProject = !project.isActive() && auth?.canInviteTalentForProject(project);
    const canShare = !position?.hasBeenFilled() || (auth.canManageClients() && project.isActive());

    const onShare = e => {
        e.currentTarget.blur();
        let params = new URLSearchParams(searchParams);
        params.set('shareTalentForRole', String(position?.id));
        return setSearchParams(params);
    }
    const canEdit = useMemo(() => {
        return project && auth?.isClientConsultant()
    }, [auth?.type, isLoading, project?.status]);

    // const [isPositionAppSheetOptionModalOpen, setIsPositionAppSheetOptionModalOpen] = useState(null)


    return (
        <div className="shortlisted-candidates-card container mx-auto max-w-[1225px]">

            <div className="role-header">

                <div className="flex flex-row items-center justify-start">
                    <h6>
                        Position {displayIndex} of {project?.sortedPositions()?.length}
                    </h6>

                    <p className={TB.mapClass("role-status", { filled: hasBeenFilled })}>
                        {hasBeenFilled ? 'POSITION FILLED' : 'POSITION NOT FILLED'}
                    </p>
                </div>
                <div className="flex flex-row items-center justify-between self-stretch flex-wrap gap-4">
                    <Link className="hover:opacity-50 transition duration-200" to={location.pathname.replace(/shortlist.*$/, `brief/role-requirements/role/${position.id}`)}>
                        <h3>{position.title}</h3>
                    </Link>

                    <ListingStatusBadge
                        position={position}
                        updatePosition={updatePosition}
                        canEdit={!project.is_on_hold && !project.isActive()}
                    />

                    <div className="flex flex-row gap-6 mlauto">

                        {/*TODO Enable App Sheet linking*/}
                        {/*{auth.canManageClients() && hasBeenFilled && (*/}
                        {/*    <div className="bg-white p-2 min-w-[200px] self-start">*/}

                        {/*        <div className="flex justify-between gap-6 items-center pb-3">*/}

                        {/*            <p className="text-base">*/}
                        {/*                App Sheet Candidate ID*/}
                        {/*            </p>*/}

                        {/*            <div className="flex gap-4 items-center">*/}
                        {/*                {position.appsheet_id && (*/}
                        {/*                    <div>*/}
                        {/*                        <button*/}
                        {/*                            disabled={isPositionUpdating}*/}
                        {/*                            id={`unlinkPosition-${position.id}`}*/}
                        {/*                            onClick={async () => {*/}
                        {/*                                try {*/}
                        {/*                                    await unlinkAppSheetForProjectPosition(position.id);*/}
                        {/*                                    await updatePosition({*/}
                        {/*                                        ...position,*/}
                        {/*                                        appsheet_id: null*/}
                        {/*                                    }, false);*/}
                        {/*                                } catch (error) {*/}
                        {/*                                    TB.alert(*/}
                        {/*                                        'error', 'Something went wrong',*/}
                        {/*                                        'Appsheet was unable to be unlinked at this time',*/}
                        {/*                                    );*/}
                        {/*                                }*/}
                        {/*                            }}*/}
                        {/*                        >*/}
                        {/*                            <i className="fas fa-unlink"/>*/}
                        {/*                        </button>*/}
                        {/*                        <InfoTooltip type="dark" target={`unlinkPosition-${position.id}`}>*/}
                        {/*                            Unlink App Sheet ID for Position*/}
                        {/*                        </InfoTooltip>*/}
                        {/*                    </div>*/}
                        {/*                )}*/}

                        {/*                <div>*/}
                        {/*                    <button*/}
                        {/*                        id={`changePosition-${position.id}`}*/}
                        {/*                        disabled={isPositionUpdating}*/}
                        {/*                        onClick={() => setIsPositionAppSheetOptionModalOpen(position.id)}*/}
                        {/*                    >*/}
                        {/*                        <i className="fas fa-sync"/>*/}
                        {/*                    </button>*/}
                        {/*                    <InfoTooltip type="dark" target={`changePosition-${position.id}`}>*/}
                        {/*                        Change App Sheet ID for Candidate*/}
                        {/*                    </InfoTooltip>*/}
                        {/*                </div>*/}
                        {/*            </div>*/}
                        {/*        </div>*/}

                        {/*        <p className="text-secondary font-semibold">*/}
                        {/*            {position.appsheet_id || 'Missing'}*/}
                        {/*        </p>*/}

                        {/*        <Modal*/}
                        {/*            okText="Submit"*/}
                        {/*            title={"Link Candidate ID to App Sheet"}*/}
                        {/*            closable={true}*/}
                        {/*            maskClosable={false}*/}
                        {/*            style={{top: 50}}*/}
                        {/*            destroyOnClose={true}*/}
                        {/*            width={700}*/}
                        {/*            footer={null}*/}
                        {/*            open={isPositionAppSheetOptionModalOpen === position.id}*/}
                        {/*            onCancel={() => setIsPositionAppSheetOptionModalOpen(false)}*/}
                        {/*            onClose={() => setIsPositionAppSheetOptionModalOpen(false)}*/}
                        {/*        >*/}
                        {/*            <PositionAppSheetOptionsModal*/}
                        {/*                onClose={() => setIsPositionAppSheetOptionModalOpen(false)}*/}
                        {/*            />*/}
                        {/*        </Modal>*/}

                        {/*    </div>*/}
                        {/*)}*/}

                        {!project.isActive() && !hasBeenFilled && canAddRemoveProjectPositions && (
                            <button onClick={() => onRequest(() => deletePosition())}
                                disabled={isDisabled || props.isDisabled}
                                className="btn btn-link-danger text-base self-center">Delete Position</button>
                        )}
                        {/*
                          * Hidden on a recorded rehire. Reopening unassigns the candidate and puts
                          * the position back out to the other shortlisted talent — but a rehire has
                          * no other candidates, and its terms were agreed off-platform with this
                          * one person specifically. There is nothing to reopen to.
                          */}
                        {!project.isActive() && hasBeenFilled && canInviteTalentForProject && !project.isHiringSourceRehire() && (
                            <button className="btn btn-outline-dark btn-outline-white self-center"
                                onClick={() => onRequest(() => reopenPosition())}
                                disabled={isDisabled || props.isDisabled}>
                                Reopen Position ({position.total_selected_candidates - 1})
                            </button>
                        )}

                        {canShare && (
                            <React.Fragment>
                                <button type="button" id="share-btn"
                                    disabled={isFetching || isDisabled || props.isDisabled}
                                    className="btn btn-primary" onClick={onShare}>
                                    <i className="fa fa-share mr-2 self-center" />
                                    Share
                                </button>
                                <InfoTooltip type="dark" target="share-btn">
                                    Share talent profiles with other team members
                                </InfoTooltip>
                            </React.Fragment>
                        )}
                    </div>

                </div>
            </div>


            {(hasBeenFilled && !overrideShowShortlist) ? (
                <React.Fragment>

                    <ProjectFormProvider>
                        <ShortlistSelectedCandidate editable isDisabled={props.isDisabled} />
                    </ProjectFormProvider>

                </React.Fragment>
            ) : (
                <div className={TB.mapClass("candidates-view", {
                    "empty": !isLoading && !error && candidateData && candidateData?.users?.length === 0
                })}>

                    <div className="candidate-stats">

                        <div className="flex flex-col gap-3 md:flex-row">
                            {TB.map(stats, (value, label) => (
                                <div key={label} className="flex-1 flex flex-col items-start justify-start">
                                    <p className="stat-label">
                                        {label}
                                    </p>
                                    <p className="stat-value">
                                        {value} {label === 'EST. HOURLY RATE' && <span className="ml-1">*</span>}
                                    </p>
                                </div>
                            ))}
                        </div>

                        {((
                            position.isFixedFee() && position.fixedFeePricingStrategyIsFullBreakdown())
                            || (position.isFixedRetainer() && position.isFixedRetainerRateBased())
                            || position.isTimeMaterials()
                        ) && (
                                <p className="text-xs pt-2 text-light-gray6">
                                    * Per average rate for role on Wripple
                                </p>
                            )}
                    </div>

                    <React.Fragment>
                        {(isLoading || (!candidateData && !error)) && (
                            <div className="flex flex-col items-stretch justify-center min-h-[100px]">
                                <LoadingBanner message="Finding Candidates" />
                            </div>
                        )}

                        {!isLoading && !error && candidateData && (
                            candidateData?.users?.length > 0 ? (
                                <React.Fragment>
                                    <div className="flex flex-row items-center justify-start">
                                        <p className="mb-0 mr-2">
                                            View:
                                        </p>
                                        <div className="wripple-off-white-bg flex flex-row items-stretch">
                                            {TB.map(ViewOptions, (icon, label) => (
                                                <button
                                                    key={label}
                                                    disabled={isDisabled}
                                                    onClick={() => setActiveView(label)}
                                                    className={TB.mapClass("btn btn-toggle-view", {
                                                        "active": activeView === label
                                                    })}>
                                                    {icon && <i className={`fas ${icon}`} />}
                                                    <span>{label}</span>
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {activeView === 'Cards' ? (
                                        <div className="candidates-list card-display [&_.card-list-item]:!h-full">
                                            <CardList
                                                fillContainer={true}
                                                minHeight={715} itemsPerRow={3}
                                                // items={TB.orderBy(candidateData?.users, 'is_assigned', 'desc')
                                                //     .map((candidate, i) => {
                                                //         if (!candidate.isShortlisted()) {
                                                //             return null;
                                                //         }
                                                //         return (
                                                //             <CandidateProvider
                                                //                 key={`${props.position.id}-talent-${candidate.id}-${props.position.id}`}
                                                //                 candidate={candidate}>
                                                //                 <CandidateTile
                                                //                     shortlistView={true}
                                                //                     candidate={candidate}
                                                //                     onRequest={onRequest}
                                                //                     isDisabled={props.isDisabled || isDisabled} />
                                                //             </CandidateProvider>
                                                //         );
                                                //     })}
                                                items={[...TB.orderBy(candidateData?.users, 'is_assigned', 'desc')
                                                    .map((candidate, i) => {
                                                        if (!candidate.isShortlisted()) {
                                                            return null;
                                                        }
                                                        return (
                                                            <CandidateProvider
                                                                key={`${props.position.id}-talent-${candidate.id}-${props.position.id}`}
                                                                candidate={candidate}
                                                            >
                                                                <CandidateTileRedesign
                                                                    shortlistView
                                                                    candidate={candidate}
                                                                    onRequest={onRequest}
                                                                    isDisabled={props.isDisabled || isDisabled}
                                                                />
                                                            </CandidateProvider>
                                                        );
                                                    })]}
                                            />
                                        </div>
                                    ) : (
                                        <ShortlistedCandidateList
                                            candidates={candidateData?.users}
                                            onRequest={onRequest}
                                            isDisabled={isDisabled || props.isDisabled} />
                                    )}

                                </React.Fragment>
                            ) : (
                                <React.Fragment>
                                    <Empty className="bg-white py-5 m-0" image={Empty.PRESENTED_IMAGE_SIMPLE} description={(
                                        <p className="font-secondary mb-0 text-center text-[14px] leading-[160%]">
                                            No shortlist talent selected</p>
                                    )}>


                                        <Link className="self-center btn btn-primary w-[150px] mx-auto"
                                            to={location.pathname.replace(/shortlist.*$/, `talent-pool/role/${position.id}`)}>
                                            <i className="fas m-t-[-2px] fa-plus mr-2" />
                                            Add Talent
                                        </Link>
                                    </Empty>
                                </React.Fragment>
                            )
                        )}

                        {!isLoading && error && (
                            <div className="flex flex-col items-stretch justify-start min-h-[100px]">
                                <ErrorBanner
                                    title="Something went wrong"
                                    message="Unable to load candidates. Please try again later"
                                    onRetry={refetch} />
                            </div>
                        )}
                    </React.Fragment>
                </div>
            )}

            <QuickHireOpenToTalentModal
                isOpen={showQuickHireModal}
                onClose={() => setShowQuickHireModal(false)}
                message="The candidate originally selected for this role has declined."
                onShowTalent={() => navigate('../talent-pool', {replace: true})}
            />
        </div>
    )
}

PositionShortlistedCandidates.propTypes = {
    isDisabled: PropTypes.bool,
    candidates: PropTypes.array,
    linkParams: PropTypes.any,
    position: PropTypes.object,
    onUpdated: PropTypes.func,
    onUpdating: PropTypes.func,
    onAssignCandidate: PropTypes.func,
    onDelete: PropTypes.func,
    onViewProposal: PropTypes.func,
    canEdit: PropTypes.bool
};
export default PositionShortlistedCandidates